import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EpisodeStage } from "@arna-med/shared";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useEpisodes, useCreateEpisode, useUpdateEpisode, useDeleteEpisode, type EpisodeDTO } from "@/hooks/api/useEpisodes";

const STAGE_LABELS: Record<EpisodeStage, string> = {
  [EpisodeStage.PrimaryExamination]: "episodes.stage.primaryExamination",
  [EpisodeStage.Investigation]: "episodes.stage.investigation",
  [EpisodeStage.Strategy]: "episodes.stage.strategy",
  [EpisodeStage.Monitoring]: "episodes.stage.monitoring",
  [EpisodeStage.Resolution]: "episodes.stage.resolution",
};

const STAGE_COLORS: Record<EpisodeStage, string> = {
  [EpisodeStage.PrimaryExamination]: "bg-blue-100 text-blue-800",
  [EpisodeStage.Investigation]: "bg-purple-100 text-purple-800",
  [EpisodeStage.Strategy]: "bg-amber-100 text-amber-800",
  [EpisodeStage.Monitoring]: "bg-orange-100 text-orange-800",
  [EpisodeStage.Resolution]: "bg-green-100 text-green-800",
};

interface Props {
  patientId: string;
}

export function EpisodesSection({ patientId }: Props) {
  const { t } = useTranslation();
  const { data: episodes = [], isLoading } = useEpisodes(patientId);
  const createMutation = useCreateEpisode();
  const updateMutation = useUpdateEpisode();
  const deleteMutation = useDeleteEpisode();

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newStage, setNewStage] = useState<EpisodeStage>(EpisodeStage.PrimaryExamination);
  const [editTarget, setEditTarget] = useState<EpisodeDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EpisodeDTO | null>(null);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      await createMutation.mutateAsync({
        patientId,
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
        stage: newStage,
      });
      setNewTitle("");
      setNewDesc("");
      setNewStage(EpisodeStage.PrimaryExamination);
      setShowCreate(false);
    } catch {
      toast.error(t("episodes.createError"));
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({
        id: editTarget.id,
        title: editTarget.title,
        description: editTarget.description,
        stage: editTarget.stage,
      });
      setEditTarget(null);
    } catch {
      toast.error(t("episodes.updateError"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteTarget.id, patientId });
      setDeleteTarget(null);
    } catch {
      toast.error(t("episodes.deleteError"));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("episodes.title")}</h2>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowCreate(true)}>
          <Plus className="h-3.5 w-3.5" />
          {t("episodes.new")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner className="h-5 w-5" />
        </div>
      ) : episodes.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            {t("episodes.empty")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {episodes.map((ep) => (
            <Card key={ep.id}>
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{ep.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STAGE_COLORS[ep.stage]}`}>
                        {t(STAGE_LABELS[ep.stage])}
                      </span>
                    </div>
                    {ep.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{ep.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(ep.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditTarget(ep)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-500" onClick={() => setDeleteTarget(ep)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("episodes.newTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t("episodes.titlePlaceholder")}
            />
            <Textarea
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder={t("episodes.descriptionPlaceholder")}
              rows={2}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("episodes.stage.label")}</label>
              <select
                value={newStage}
                onChange={(e) => setNewStage(e.target.value as EpisodeStage)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {Object.values(EpisodeStage).map((stage) => (
                  <option key={stage} value={stage}>{t(STAGE_LABELS[stage])}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCreate} disabled={!newTitle.trim() || createMutation.isPending}>
              {createMutation.isPending && <Spinner className="h-3 w-3 mr-1" />}
              {t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("episodes.editTitle")}</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <Input
                value={editTarget.title}
                onChange={(e) => setEditTarget({ ...editTarget, title: e.target.value })}
                placeholder={t("episodes.titlePlaceholder")}
              />
              <Textarea
                value={editTarget.description ?? ""}
                onChange={(e) => setEditTarget({ ...editTarget, description: e.target.value || null })}
                placeholder={t("episodes.descriptionPlaceholder")}
                rows={2}
              />
              <div className="space-y-1">
                <label className="text-sm font-medium">{t("episodes.stage.label")}</label>
                <select
                  value={editTarget.stage}
                  onChange={(e) => setEditTarget({ ...editTarget, stage: e.target.value as EpisodeStage })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {Object.values(EpisodeStage).map((stage) => (
                    <option key={stage} value={stage}>{t(STAGE_LABELS[stage])}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>{t("common.cancel")}</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Spinner className="h-3 w-3 mr-1" />}
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("episodes.deleteConfirm")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Spinner className="h-3 w-3 mr-1" />}
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
